import { NtaFile } from './file.typings';

export type CaseMix = 'NA' | 'NB' | 'NC' | 'ND' | 'NE' | 'NF' | 'NR';

export type PccSyncItem = 'progressNotes' | 'patientInformation';

export type NtaPatientStatus = 'New' | 'Current' | 'Discharged' | 'OnLeave';

export const NtaPatientStatusOptions: NtaPatientStatus[] = ['New', 'Current', 'Discharged', 'OnLeave'] as const;

export type RoundingStatus = 'overdue' | 'soon' | 'na' | 'upcoming';

export const RoundingStatusOptions: Array<{ value: RoundingStatus; label: string; priority: number }> = [
	{ value: 'overdue', label: 'Overdue', priority: 0 },
	{ value: 'soon', label: 'Soon', priority: 1 },
	{ value: 'na', label: 'Awaiting First Round', priority: 2 },
	{ value: 'upcoming', label: 'Upcoming', priority: 3 },
];

export class NtaPatient {

	patientId = '';

	pccPatientId: number;

	facilityId = '';

	pccFacilityId: number;

	pccOrgUuid = '';

	summary = '';

	aiResponsesCounts: Record<string, number> = {};

	summarizing = false;

	summarizingStartDateTime: Date;

	summarizingCompleteDateTime: Date;

	fullName = '';

	firstName = '';

	middleName = '';

	lastName = '';

	suffix = '';

	birthdate: string;

	createDateTime: Date;

	latestUploadDate: Date;

	reviewBeginDate: Date;

	reviewCompleteDate: Date;

	reviewDays: number;

	admitDate: string;

	admitSource = '';

	dischargeDate?: string;

	patientStatus: NtaPatientStatus;

	isOnLeave?: boolean;

	pccMedicalRecordNumber?: string;

	lastOpenedDate = new Date();

	files: NtaFile[] = [];

	pageCount = 0;

	scanningFileCount = 0;

	pdfUrl: string;

	hasFullPdf: boolean;

	caseMixStatus = '';

	hippsCode = 'ZZZZZ';

	approvedCount = 0;

	approvedScore = 0;

	rejectedCount = 0;

	rejectedScore = 0;

	progress = 0;

	legacyCaseMixStatus = '';

	legacyApprovedCount = 0;

	legacyApprovedScore = 0;

	legacyRejectedCount = 0;

	legacyRejectedScore = 0;

	legacyProgress = 0;

	foundCategoryCount = 0;

	status: 'unreviewed' | 'in progress' | 'complete' = 'unreviewed';

	updating = false;

	error: boolean;

	fileCount = 0;

	createdBy: string;

	createdByName: string;

	pdf: Blob;

	isLegacyPatient = false;

	needsBigQuerySync: 'created' | 'updated' | null = null;

	gender?: 'male' | 'female' | '';

	lengthOfStay?: number;

	medicareNumber?: number;

	assessmentType?: 'pps' | 'ips';

	requiredPccSyncItems: PccSyncItem[] = [];

	facilityLocation?: {
		roomDesc?: string;
		roomId?: number;
		roomType?: string;
		floorDesc?: string;
		floorId?: number;
		unitDesc?: string;
		unitId?: number;
		bedDesc?: string;
		bedId?: number;
	} = {};

	roundingScheduleId?: string;

	roundingStatus: RoundingStatus;

	nextRoundTime: Date;

	lastRoundTime: Date;

	activeOverdueTaskId: string | null = null;

	updatingRoundTime = false;

	hasAdrPackets = false;

	adrPacketsCount = 0;

	latestAdrPacketStatus?: string;

	latestAdrPacketStatusUpdatedAt?: Date;

	latestAdrPacketStatusUpdatedByName?: string;

	analyticsDate?: string;

	runningEmbeddingModelUpdate = false;

	scheduledRembeddingsCount = 0;

	reembeddingsCompletedCount = 0;

	targetEmbeddingModel?: string;

	embeddingModel?: string;

	embeddingModelUpdatedAt?: Date;

	get profileImageUrl() {
		const name = this.fullName;
		return `https://api.dicebear.com/5.x/initials/svg?seed=${name}&backgroundColor=007AC7,004E80,003557,22E9B6,0E8D6C,095A45,b91c1c,991b1b,7f1d1d`;
	}

	constructor(partial?:Partial<NtaPatient>) {
		if (partial) {
			const cleanedPartial: Partial<NtaPatient> = Object.entries(partial)
				.filter(([_, value]) => value !== null && value !== undefined)
				.reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});
			Object.assign(this, cleanedPartial);
			['birthdate', 'admitDate', 'lastOpenedDate', 'createDateTime', 'latestUploadDate', 'reviewBeginDate', 'reviewCompleteDate', 'summarizingStartDateTime', 'summarizingCompleteDateTime', 'nextRoundTime', 'lastRoundTime', 'embeddingModelUpdatedAt', 'latestAdrPacketStatusUpdatedAt'].forEach((key) => {
				if (cleanedPartial[key]?.toDate) {
					this[key] = partial[key].toDate();
				}
			});
			this.aiResponsesCounts = cleanedPartial.aiResponsesCounts ?? {};
			this.isLegacyPatient = (cleanedPartial.legacyProgress ?? 0) > 0 && !(cleanedPartial.progress ?? 0);
			if (this.firstName && this.lastName) {
				const coreName = [this.lastName, this.firstName].filter(Boolean).join(', ');

				const extensions = [this.middleName, this.suffix].filter(Boolean).join(' ');

				this.fullName = [coreName, extensions].filter(Boolean).join(' ');
			}
			if (!this.fullName && (partial as any).name) this.fullName = (partial as any).name;
			if (!this.hippsCode) this.hippsCode = 'ZZZZZ';
		}
	}
}
